import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Icon from "./Icon";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let openCount = 0;

/**
 * Accessible dialog rendered in a portal.
 * variant: "dialog" (centred) | "sheet" (bottom sheet on mobile, centred on desktop)
 *          | "drawer" (left side panel) | "fullscreen"
 * Traps focus, closes on Escape / backdrop, restores focus, locks page scroll.
 */
function Modal({
  open,
  onClose,
  title,
  hideTitle,
  closeButton = !hideTitle,
  variant = "dialog",
  children,
  footer,
  labelledBy,
  className = "",
}) {
  const [rendered, setRendered] = useState(open);
  const [closing, setClosing] = useState(false);
  const panelRef = useRef(null);
  const restoreRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useRef(`v-modal-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => {
    if (open) {
      setRendered(true);
      setClosing(false);
    } else if (rendered) {
      setClosing(true);
      const timer = setTimeout(() => {
        setRendered(false);
        setClosing(false);
      }, 180);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement;
    openCount += 1;
    document.body.classList.add("v-scroll-lock");

    const frame = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const auto = panel.querySelector("[data-autofocus]");
      const first = auto || panel.querySelector(FOCUSABLE);
      (first || panel).focus();
    });

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current && onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter(
        (node) => node.offsetParent !== null
      );
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
      openCount -= 1;
      if (openCount <= 0) {
        openCount = 0;
        document.body.classList.remove("v-scroll-lock");
      }
      const restore = restoreRef.current;
      if (restore && typeof restore.focus === "function") restore.focus();
    };
  }, [open]);

  if (!rendered || typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <div className={`v-modal v-modal--${variant} ${closing ? "is-closing" : ""} ${className}`}>
      <div className="v-modal__backdrop" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="v-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy || (title ? titleId : undefined)}
        tabIndex={-1}
      >
        {title && hideTitle ? (
          <h2 id={titleId} className="v-sr-only">
            {title}
          </h2>
        ) : null}
        {(title && !hideTitle) || closeButton ? (
          <div className={`v-modal__head ${hideTitle ? "v-modal__head--bare" : ""}`}>
            {!hideTitle ? (
              <h2 id={titleId} className="v-modal__title">
                {title}
              </h2>
            ) : null}
            {closeButton ? (
              <button type="button" className="v-icon-btn v-modal__close" onClick={onClose} aria-label="Close">
                <Icon name="close" />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="v-modal__body">{children}</div>
        {footer ? <div className="v-modal__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
